/* assets/littleg-family-address-check.js — FAMILY-ADDRESS-CHECK-RUNTIME-03
 * HTML owner: snippets/littleg-family-address-check.liquid.
 * Placement owner: sections/littleg-family-account.liquid.
 * Boundary: local draft excludes precise coordinates, files and contact details.
 *
 * Responsibilities:
 * 1. 8-Step Wizard State Machine & LocalStorage Persistence.
 * 2. Accessible Focus Management & Live Status Announcements.
 * 3. Leaflet Map Initialization, Marker Sync & Layout Resizing.
 * 4. Evidence File Validation, Priorities Limit & Case Submission.
 */
(function (window, document) {
  'use strict';

  var VERSION = '2026-09-21-family-address-check-runtime-03';
  var ROOT_SELECTOR = '[data-family-address-check]';
  var FAMILY_ROOT_SELECTOR = '[data-littleg-family-account]';
  var STORAGE_KEY = 'littleg:family-address-check:v1';
  var STEP_IDS = ['S01', 'S02', 'S03', 'S04', 'S05', 'S06', 'S07', 'S08'];
  var MAX_PRIORITIES = 3;
  var MAP_DEFAULT = [22.3193, 114.1694];

  var PRIORITIES = {
    school_access: { label: '上學方便', scope: ['學校位置', '接送距離'] },
    commute_time: { label: '通勤時間', scope: ['通勤距離', '交通方式'] },
    walking_safety: { label: '步行安全', scope: ['步行路線', '過路安全'] },
    public_transport: { label: '公共交通', scope: ['巴士與鐵路', '轉乘選擇'] },
    nearby_construction: { label: '附近施工', scope: ['規劃與工程', '噪音影響'] },
    child_facilities: { label: '兒童設施', scope: ['公園與遊樂場', '家庭設施'] },
    elder_care: { label: '長者照護', scope: ['長者服務', '醫療配套'] },
    medical_access: { label: '醫療方便', scope: ['診所與醫院', '就醫距離'] },
    accessibility: { label: '無障礙', scope: ['升降機與樓梯', '斜路與輪椅'] },
    property_value: { label: '物業價值', scope: ['樓宇資料', '物業公開資料'] },
  };

  var PLACE_LABELS = {
    school: '孩子學校',
    work: '上班地點',
    caregiver: '照顧者住處',
    medical: '常去醫療地點',
  };

  var RESULT_CATEGORIES = [
    { key: 'school_access', label: '上學與接送' },
    { key: 'commute_transport', label: '通勤交通' },
    { key: 'family_facilities', label: '家庭設施' },
    { key: 'walking_safety', label: '步行安全' },
    { key: 'planning_works', label: '規劃工程' },
    { key: 'building_property', label: '樓宇與物業' },
  ];

  if (window.__LITTLEG_FAMILY_ADDRESS_CHECK__ === VERSION) return;
  window.__LITTLEG_FAMILY_ADDRESS_CHECK__ = VERSION;

  var lastPanelOpener = null;

  function clean(value) {
    return String(value == null ? '' : value).trim();
  }

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function safeLocal() {
    try {
      return window.localStorage || null;
    } catch (_) {
      return null;
    }
  }

  function uid(prefix) {
    try {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return prefix + '-' + window.crypto.randomUUID();
      }
    } catch (_) {}
    return prefix + '-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8);
  }

  function emptyState() {
    return {
      version: 1,
      step: 'S01',
      compareMode: false,
      decisionType: '',
      property: {
        query: '',
        displayName: '',
        district: '',
        latitude: null,
        longitude: null,
        confirmedByUser: false,
      },
      evidence: {
        sourceType: '',
        fileName: '',
        fileSize: 0,
        listingUrl: '',
        fields: {},
      },
      household: {
        places: [],
        travelModes: [],
        mobilityPreferences: [],
      },
      priorities: [],
      customPriority: '',
      skippedOptionalFields: [],
      result: null,
      updatedAt: '',
    };
  }

  function normalizeState(raw) {
    var base = emptyState();
    var source = raw && typeof raw === 'object' ? raw : {};
    base.step = STEP_IDS.indexOf(source.step) >= 0 ? source.step : 'S01';
    base.compareMode = source.compareMode === true;
    base.decisionType = clean(source.decisionType);
    base.property.query = clean(source.property && source.property.query);
    base.property.displayName = clean(source.property && source.property.displayName);
    base.property.district = clean(source.property && source.property.district);
    base.property.latitude = Number.isFinite(Number(source.property && source.property.latitude))
      ? Number(source.property.latitude)
      : null;
    base.property.longitude = Number.isFinite(Number(source.property && source.property.longitude))
      ? Number(source.property.longitude)
      : null;
    base.property.confirmedByUser = !!(source.property && source.property.confirmedByUser);
    base.evidence.sourceType = clean(source.evidence && source.evidence.sourceType);
    base.evidence.fileName = clean(source.evidence && source.evidence.fileName);
    base.evidence.fileSize = Number(source.evidence && source.evidence.fileSize) || 0;
    base.evidence.listingUrl = clean(source.evidence && source.evidence.listingUrl);
    base.evidence.fields =
      source.evidence && source.evidence.fields && typeof source.evidence.fields === 'object'
        ? source.evidence.fields
        : {};
    base.household.places = Array.isArray(source.household && source.household.places)
      ? source.household.places
          .slice(0, 12)
          .map(function (place) {
            return {
              id: clean(place.id) || uid('place'),
              type: PLACE_LABELS[clean(place.type)] ? clean(place.type) : 'work',
              label: clean(place.label).slice(0, 80),
              address: clean(place.address).slice(0, 160),
              latitude: Number.isFinite(Number(place.latitude)) ? Number(place.latitude) : null,
              longitude: Number.isFinite(Number(place.longitude)) ? Number(place.longitude) : null,
            };
          })
          .filter(function (place) {
            return place.label;
          })
      : [];
    base.household.travelModes = Array.isArray(source.household && source.household.travelModes)
      ? source.household.travelModes.map(clean).filter(Boolean).slice(0, 4)
      : [];
    base.household.mobilityPreferences = Array.isArray(
      source.household && source.household.mobilityPreferences
    )
      ? source.household.mobilityPreferences.map(clean).filter(Boolean).slice(0, 6)
      : [];
    base.priorities = Array.isArray(source.priorities)
      ? source.priorities
          .filter(function (key) {
            return !!PRIORITIES[key];
          })
          .slice(0, MAX_PRIORITIES)
      : [];
    base.customPriority = clean(source.customPriority).slice(0, 300);
    base.skippedOptionalFields = Array.isArray(source.skippedOptionalFields)
      ? source.skippedOptionalFields.map(clean).filter(Boolean).slice(0, 20)
      : [];
    base.updatedAt = clean(source.updatedAt);
    return base;
  }

  function readDraft() {
    var store = safeLocal();
    if (!store) return emptyState();
    try {
      return normalizeState(JSON.parse(store.getItem(STORAGE_KEY) || 'null'));
    } catch (_) {
      return emptyState();
    }
  }

  function writeDraft(state) {
    var store = safeLocal();
    if (!store) return false;
    try {
      var safe = normalizeState(state);
      safe.step = state.step;
      safe.updatedAt = new Date().toISOString();
      store.setItem(STORAGE_KEY, JSON.stringify(safe));
      return true;
    } catch (_) {
      return false;
    }
  }

  function clearDraft() {
    var store = safeLocal();
    if (!store) return false;
    try {
      store.removeItem(STORAGE_KEY);
      return true;
    } catch (_) {
      return false;
    }
  }

  function setStatus(root, message, state) {
    var node = root.querySelector('[data-family-address-status]');
    if (!node) return;
    node.textContent = clean(message);
    if (state) node.setAttribute('data-state', state);
    else node.removeAttribute('data-state');
  }

  function announce(root, message, state) {
    setStatus(root, message, state);
    if (!message) return;
    window.clearTimeout(root.__familyStatusTimer);
    root.__familyStatusTimer = window.setTimeout(function () {
      var node = root.querySelector('[data-family-address-status]');
      if (node && node.textContent === message) {
        node.textContent = '';
        node.removeAttribute('data-state');
      }
    }, 4200);
  }

  function stepIndex(step) {
    var index = STEP_IDS.indexOf(step);
    return index >= 0 ? index : 0;
  }

  function refreshMapCanvasSize(root) {
    var store = getMapStore(root);
    Object.keys(store).forEach(function (key) {
      if (store[key] && store[key].map) {
        window.setTimeout(function () {
          try {
            store[key].map.invalidateSize();
          } catch (_) {}
        }, 150);
      }
    });
  }

  function setScreen(root, state, step, options) {
    var next = STEP_IDS.indexOf(step) >= 0 ? step : 'S01';
    state.step = next;
    root.setAttribute('data-family-address-step', next);

    Array.prototype.slice
      .call(root.querySelectorAll('[data-family-address-screen]'))
      .forEach(function (screen) {
        screen.hidden = screen.getAttribute('data-family-address-screen') !== next;
      });

    var progress = root.querySelector('[data-family-address-progress]');
    if (progress) progress.textContent = stepIndex(next) + 1 + ' / ' + STEP_IDS.length;

    var back = root.querySelector('[data-family-address-back]');
    if (back) back.hidden = next === 'S01';

    var saveExit = root.querySelector('[data-family-address-save-exit]');
    if (saveExit)
      saveExit.hidden =
        next === 'S08' &&
        !!root.querySelector('[data-family-case-state="submitted"]:not([hidden])');

    if (!options || options.persist !== false) writeDraft(state);

    if (!options || options.scroll !== false) {
      try {
        root.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch (_) {
        root.scrollIntoView();
      }
    }

    // 螢幕閱讀器焦點切換
    var activeScreen = root.querySelector('[data-family-address-screen="' + next + '"]');
    var heading = activeScreen ? activeScreen.querySelector('h2') : null;
    if (heading) {
      heading.setAttribute('tabindex', '-1');
      window.requestAnimationFrame(function () {
        heading.focus({ preventScroll: true });
      });
    }

    if (next === 'S01') syncEntryState(root, state);
    if (next === 'S02') initPropertyMap(root, state);
    if (next === 'S04') initRouteMap(root, state);
    if (next === 'S05') renderPriorities(root, state);
    if (next === 'S06') renderReview(root, state);
    if (next === 'S07') renderResult(root, state);
    if (next === 'S08') renderBrief(root, state);

    refreshMapCanvasSize(root);
  }

  function openWorkflow(familyRoot, root, state) {
    familyRoot.setAttribute('data-family-address-open', 'true');
    root.hidden = false;
    root.setAttribute('data-family-address-state', 'open');
    setScreen(root, state, state.step || 'S01', { persist: false, scroll: true });
  }

  function closeWorkflow(familyRoot, root, state, save) {
    if (save) writeDraft(state);
    familyRoot.removeAttribute('data-family-address-open');
    root.hidden = true;
    root.setAttribute('data-family-address-state', 'closed');
    closePanel(root);
    var opener = familyRoot.querySelector('[data-family-address-open]');
    if (opener && typeof opener.focus === 'function') opener.focus();
  }

  function createText(tag, text, className) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    node.textContent = text;
    return node;
  }

  function openPanel(root, title, contentBuilder, openerElement) {
    lastPanelOpener = openerElement || document.activeElement;
    var panel = root.querySelector('[data-family-address-panel]');
    var backdrop = root.querySelector('[data-family-address-panel-backdrop]');
    var heading = root.querySelector('[data-family-address-panel-title]');
    var body = root.querySelector('[data-family-address-panel-body]');
    if (!panel || !backdrop || !heading || !body) return;
    heading.textContent = title;
    body.replaceChildren();
    if (typeof contentBuilder === 'function') contentBuilder(body);
    backdrop.hidden = false;
    panel.hidden = false;
    document.body.setAttribute('data-family-address-panel-open', 'true');
    window.requestAnimationFrame(function () {
      heading.setAttribute('tabindex', '-1');
      heading.focus();
    });
  }

  function closePanel(root) {
    var panel = root.querySelector('[data-family-address-panel]');
    var backdrop = root.querySelector('[data-family-address-panel-backdrop]');
    if (panel) panel.hidden = true;
    if (backdrop) backdrop.hidden = true;
    document.body.removeAttribute('data-family-address-panel-open');
    if (lastPanelOpener && typeof lastPanelOpener.focus === 'function') {
      lastPanelOpener.focus();
      lastPanelOpener = null;
    }
  }

  function getMapStore(root) {
    if (!root.__familyAddressMaps) root.__familyAddressMaps = {};
    return root.__familyAddressMaps;
  }

  function mapAvailable() {
    return !!(window.L && typeof window.L.map === 'function');
  }

  function createMap(root, key, center, onClick) {
    var store = getMapStore(root);
    if (store[key] && store[key].map) return store[key];
    var container = root.querySelector('[data-family-address-map="' + key + '"]');
    if (!container || !mapAvailable()) return null;
    var map = window.L.map(container, { zoomControl: true, scrollWheelZoom: false }).setView(
      center || MAP_DEFAULT,
      14
    );
    window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap contributors',
    }).addTo(map);
    if (typeof onClick === 'function') map.on('click', onClick);
    store[key] = { map: map, marker: null, pendingMarker: null, placeMarkers: [] };
    window.setTimeout(function () {
      try {
        map.invalidateSize();
      } catch (_) {}
    }, 150);
    return store[key];
  }

  function setPrimaryMarker(mapState, lat, lng, draggable, onDrag) {
    if (!mapState || !mapState.map || !Number.isFinite(lat) || !Number.isFinite(lng)) return;
    if (!mapState.marker) {
      mapState.marker = window.L.marker([lat, lng], { draggable: !!draggable }).addTo(mapState.map);
      if (draggable && typeof onDrag === 'function') {
        mapState.marker.on('dragend', function () {
          var position = mapState.marker.getLatLng();
          onDrag(position.lat, position.lng);
        });
      }
    } else {
      mapState.marker.setLatLng([lat, lng]);
    }
    mapState.map.setView([lat, lng], Math.max(mapState.map.getZoom(), 15));
  }

  function updateSelectedLocation(root, state) {
    var card = root.querySelector('[data-family-selected-location]');
    var name = root.querySelector('[data-family-selected-location-name]');
    var coordinates = root.querySelector('[data-family-selected-location-coordinates]');
    var confirm = root.querySelector('[data-family-address-action="confirm-address"]');
    var ready = !!(
      state.property.displayName &&
      Number.isFinite(state.property.latitude) &&
      Number.isFinite(state.property.longitude)
    );
    if (card) card.hidden = !ready;
    if (name) name.textContent = state.property.displayName || '';
    if (coordinates) coordinates.textContent = ready ? '位置已在地圖確認' : '';
    if (confirm) confirm.disabled = !ready;
  }

  function initPropertyMap(root, state) {
    var mapState = createMap(
      root,
      'property',
      Number.isFinite(state.property.latitude)
        ? [state.property.latitude, state.property.longitude]
        : MAP_DEFAULT,
      function (event) {
        state.property.latitude = event.latlng.lat;
        state.property.longitude = event.latlng.lng;
        state.property.confirmedByUser = false;
        if (!state.property.displayName)
          state.property.displayName = state.property.query || '地圖選定位置';
        setPrimaryMarker(mapState, event.latlng.lat, event.latlng.lng, true, function (lat, lng) {
          state.property.latitude = lat;
          state.property.longitude = lng;
          state.property.confirmedByUser = false;
          updateSelectedLocation(root, state);
        });
        updateSelectedLocation(root, state);
      }
    );
    if (!mapState) {
      setStatus(root, '地圖資產尚未載入；仍可稍後重試。', 'error');
      return;
    }
    if (Number.isFinite(state.property.latitude) && Number.isFinite(state.property.longitude)) {
      setPrimaryMarker(
        mapState,
        state.property.latitude,
        state.property.longitude,
        true,
        function (lat, lng) {
          state.property.latitude = lat;
          state.property.longitude = lng;
          state.property.confirmedByUser = false;
          updateSelectedLocation(root, state);
        }
      );
    }
    updateSelectedLocation(root, state);
  }

  function renderAddressResults(root, state) {
    var list = root.querySelector('[data-family-address-results]');
    if (!list) return;
    list.replaceChildren();
    var query = clean(state.property.query);
    if (!query) return;
    var districtSelect = root.querySelector('[data-family-address-district]');
    var district = districtSelect
      ? clean(
          districtSelect.options[districtSelect.selectedIndex] &&
            districtSelect.options[districtSelect.selectedIndex].text
        )
      : '';
    [query, query + ' 附近', query + '（請在地圖確認）'].forEach(function (label, index) {
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'lg-family-address__address-result';
      button.setAttribute('data-family-address-candidate', label);
      button.append(createText('span', '⌖'));
      var copy = document.createElement('span');
      copy.append(
        createText('strong', label),
        createText('small', index === 0 ? district || '地址文字匹配' : '需要在地圖確認位置')
      );
      button.append(copy, createText('span', '›'));
      list.appendChild(button);
    });
  }

  function selectAddressCandidate(root, state, label) {
    state.property.displayName = clean(label);
    state.property.query = clean(label);
    state.property.confirmedByUser = false;
    var input = root.querySelector('[data-family-address-search]');
    if (input) input.value = state.property.query;
    renderAddressResults(root, state);
    initPropertyMap(root, state);
    announce(root, '地址文字已選擇，請在地圖上確認實際位置。');
  }

  function handleEvidenceFile(root, state, input) {
    var file = input && input.files && input.files[0];
    if (!file) return;
    var maxBytes = 25 * 1024 * 1024;
    if (file.size > maxBytes) {
      input.value = '';
      announce(root, '檔案超過 25MB，請選擇較小的文件。', 'error');
      return;
    }
    state.evidence.sourceType = clean(input.getAttribute('data-family-evidence-input'));
    state.evidence.fileName = clean(file.name);
    state.evidence.fileSize = Number(file.size) || 0;
    var preview = root.querySelector('[data-family-evidence-preview]');
    if (preview) {
      preview.replaceChildren();
      if (file.type.indexOf('image/') === 0) {
        var image = document.createElement('img');
        image.alt = '已選樓盤文件預覽';
        image.src = URL.createObjectURL(file);
        image.onload = function () {
          try {
            URL.revokeObjectURL(image.src);
          } catch (_) {}
        };
        preview.appendChild(image);
      } else {
        preview.append(createText('strong', file.name), createText('p', formatBytes(file.size)));
      }
    }
    announce(root, '已加入文件：' + file.name, 'success');
  }

  function formatBytes(bytes) {
    var value = Number(bytes) || 0;
    if (value < 1024) return value + ' B';
    if (value < 1024 * 1024) return Math.round(value / 1024) + ' KB';
    return (value / (1024 * 1024)).toFixed(1) + ' MB';
  }

  function collectEvidence(root, state) {
    var fields = {};
    Array.prototype.slice
      .call(root.querySelectorAll('[data-family-evidence-field]'))
      .forEach(function (field) {
        var key = clean(field.getAttribute('data-family-evidence-field'));
        var input = field.querySelector('input');
        var value = clean(input && input.value);
        fields[key] = {
          value: value,
          recognitionState: value ? 'found' : 'not_found',
          guardianState: value ? 'confirmed' : 'pending',
        };
        field.setAttribute('data-confirmed', value ? 'true' : 'false');
        var stateNode = field.querySelector('[data-family-evidence-state]');
        if (stateNode) stateNode.textContent = value ? '已確認' : '待確認';
      });
    state.evidence.fields = fields;
    var listingUrl = root.querySelector('[data-family-listing-url]');
    state.evidence.listingUrl = clean(listingUrl && listingUrl.value);
    var confirmed = Object.keys(fields).filter(function (key) {
      return fields[key].guardianState === 'confirmed';
    }).length;
    var count = root.querySelector('[data-family-evidence-count]');
    if (count) count.textContent = confirmed + ' / ' + Object.keys(fields).length + ' 已確認';
    return confirmed;
  }

  function initRouteMap(root, state) {
    var center = Number.isFinite(state.property.latitude)
      ? [state.property.latitude, state.property.longitude]
      : MAP_DEFAULT;
    var mapState = createMap(root, 'routes', center, function (event) {
      mapState.pendingLatLng = { latitude: event.latlng.lat, longitude: event.latlng.lng };
      if (mapState.pendingMarker) mapState.pendingMarker.setLatLng(event.latlng);
      else mapState.pendingMarker = window.L.marker(event.latlng).addTo(mapState.map);
      var add = root.querySelector('[data-family-place-add]');
      if (add) add.disabled = false;
      announce(root, '已設定下一個家庭地點的位置。');
    });
    if (!mapState) return;
    renderRouteMapMarkers(root, state);
  }

  function renderRouteMapMarkers(root, state) {
    var mapState = getMapStore(root).routes;
    if (!mapState || !mapState.map) return;
    mapState.placeMarkers.forEach(function (marker) {
      try {
        mapState.map.removeLayer(marker);
      } catch (_) {}
    });
    mapState.placeMarkers = [];
    if (Number.isFinite(state.property.latitude) && Number.isFinite(state.property.longitude)) {
      var home = window.L.marker([state.property.latitude, state.property.longitude])
        .addTo(mapState.map)
        .bindPopup(escapeHtml('住家位置'));
      mapState.placeMarkers.push(home);
    }
    state.household.places.forEach(function (place) {
      if (!Number.isFinite(place.latitude) || !Number.isFinite(place.longitude)) return;
      var marker = window.L.marker([place.latitude, place.longitude])
        .addTo(mapState.map)
        .bindPopup(escapeHtml(place.label));
      mapState.placeMarkers.push(marker);
    });
    var points = mapState.placeMarkers.map(function (marker) {
      return marker.getLatLng();
    });
    if (points.length > 1) {
      try {
        mapState.map.fitBounds(window.L.latLngBounds(points), { padding: [30, 30] });
      } catch (_) {}
    }
  }

  function renderPlaces(root, state) {
    var list = root.querySelector('[data-family-places]');
    if (!list) return;
    list.replaceChildren();
    state.household.places.forEach(function (place) {
      var card = document.createElement('article');
      card.className = 'lg-family-address__place-card';
      card.setAttribute('data-family-place', place.id);
      card.append(
        createText(
          'span',
          place.type === 'school'
            ? '◇'
            : place.type === 'medical'
              ? '✚'
              : place.type === 'caregiver'
                ? '⌂'
                : '▣'
        )
      );
      var copy = document.createElement('span');
      copy.append(
        createText('strong', place.label),
        createText(
          'small',
          (PLACE_LABELS[place.type] || '家庭地點') + (place.address ? ' · ' + place.address : '')
        )
      );
      var remove = createText('button', '×');
      remove.type = 'button';
      remove.setAttribute('data-family-place-remove', place.id);
      remove.setAttribute('aria-label', '移除' + place.label);
      card.append(copy, remove);
      list.appendChild(card);
    });
    renderRouteRows(root, state);
    renderRouteMapMarkers(root, state);
  }

  function radians(value) {
    return (value * Math.PI) / 180;
  }

  function distanceMeters(a, b) {
    if (
      !a ||
      !b ||
      !Number.isFinite(a.latitude) ||
      !Number.isFinite(a.longitude) ||
      !Number.isFinite(b.latitude) ||
      !Number.isFinite(b.longitude)
    )
      return null;
    var earth = 6371000;
    var dLat = radians(b.latitude - a.latitude);
    var dLng = radians(b.longitude - a.longitude);
    var lat1 = radians(a.latitude);
    var lat2 = radians(b.latitude);
    var h =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return earth * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
  }

  function estimateMinutes(distance, mode) {
    if (!Number.isFinite(distance)) return null;
    if (mode === 'walk') return Math.max(1, Math.round(distance / 78));
    if (mode === 'transit') return Math.max(5, Math.round(distance / 260) + 7);
    return Math.max(3, Math.round(distance / 430) + 3);
  }

  function renderRouteRows(root, state) {
    var host = root.querySelector('[data-family-route-rows]');
    if (!host) return;
    host.replaceChildren();
    var home = state.property;
    if (!state.household.places.length || !Number.isFinite(home.latitude)) {
      host.appendChild(
        createText('p', '加入並定位家庭地點後顯示初步估算。', 'lg-family-address__empty')
      );
      return;
    }
    state.household.places.forEach(function (place) {
      var distance = distanceMeters(home, place);
      var row = document.createElement('div');
      row.className = 'lg-family-address__route-row';
      row.append(
        createText('strong', place.label),
        createText('span', estimateMinutes(distance, 'walk') + ' 分'),
        createText('span', estimateMinutes(distance, 'transit') + ' 分'),
        createText('span', estimateMinutes(distance, 'drive') + ' 分')
      );
      row.setAttribute('title', '按直線距離推算，不代表實際交通路線');
      host.appendChild(row);
    });
  }

  function selectedValues(root, name) {
    return Array.prototype.slice
      .call(root.querySelectorAll('[name="' + name + '"]:checked'))
      .map(function (input) {
        return clean(input.value);
      });
  }

  function renderPriorities(root, state) {
    Array.prototype.slice
      .call(root.querySelectorAll('[name="family_address_priority"]'))
      .forEach(function (input) {
        input.checked = state.priorities.indexOf(input.value) >= 0;
      });
    var count = root.querySelector('[data-family-priority-count]');
    if (count) count.textContent = state.priorities.length + ' / ' + MAX_PRIORITIES;
    var summary = root.querySelector('[data-family-priority-summary]');
    if (summary) {
      summary.replaceChildren();
      if (!state.priorities.length) summary.appendChild(createText('li', '尚未選擇'));
      state.priorities.forEach(function (key) {
        summary.appendChild(createText('li', PRIORITIES[key].label));
      });
      if (state.customPriority) summary.appendChild(createText('li', state.customPriority));
    }
    var scope = root.querySelector('[data-family-analysis-scope]');
    if (scope) {
      scope.replaceChildren();
      var seen = {};
      state.priorities.forEach(function (key) {
        PRIORITIES[key].scope.forEach(function (item) {
          if (seen[item]) return;
          seen[item] = true;
          scope.appendChild(createText('li', item));
        });
      });
      if (!scope.children.length) scope.appendChild(createText('li', '按選擇顯示資料範圍'));
    }
  }

  function buildReview(state) {
    var evidenceValues = Object.keys(state.evidence.fields || {}).filter(function (key) {
      return clean(state.evidence.fields[key] && state.evidence.fields[key].value);
    });
    return [
      {
        key: 'address',
        label: '地址',
        state: state.property.confirmedByUser ? 'confirmed' : 'missing_required',
        required: true,
        lines: [
          state.property.displayName || '尚未確認地址',
          state.property.confirmedByUser ? '位置已在地圖確認' : '需要返回確認位置',
        ],
      },
      {
        key: 'property',
        label: '樓盤資料',
        state:
          evidenceValues.length || state.evidence.listingUrl || state.evidence.fileName
            ? 'confirmed'
            : 'missing_optional',
        required: false,
        lines: [
          state.evidence.fileName || state.evidence.listingUrl || '未提供樓盤文件',
          evidenceValues.length + ' 個欄位已確認',
        ],
      },
      {
        key: 'places',
        label: '家庭地點',
        state: state.household.places.length ? 'confirmed' : 'missing_optional',
        required: false,
        lines: state.household.places.length
          ? state.household.places
              .map(function (place) {
                return place.label;
              })
              .slice(0, 4)
          : ['尚未加入家庭地點'],
      },
      {
        key: 'priorities',
        label: '關注重點',
        state: state.priorities.length ? 'confirmed' : 'missing_required',
        required: true,
        lines: state.priorities.length
          ? state.priorities.map(function (key) {
              return PRIORITIES[key].label;
            })
          : ['至少選擇一項'],
      },
    ];
  }

  function renderReview(root, state) {
    var review = buildReview(state);
    var grid = root.querySelector('[data-family-review-grid]');
    var banner = root.querySelector('[data-family-review-missing]');
    var summary = root.querySelector('[data-family-review-summary]');
    if (grid) {
      grid.replaceChildren();
      review.forEach(function (section) {
        var card = document.createElement('article');
        card.className = 'lg-family-address__review-card';
        card.setAttribute('data-review-key', section.key);
        card.setAttribute('data-review-state', section.state);
        var heading = document.createElement('div');
        heading.className = 'lg-family-address__section-heading';
        heading.append(
          createText('h3', section.label),
          createText(
            'span',
            section.state === 'confirmed' ? '已確認' : section.required ? '必須補充' : '可略過',
            'lg-family-address__badge'
          )
        );
        var list = document.createElement('ul');
        section.lines.forEach(function (line) {
          list.appendChild(createText('li', line));
        });
        var edit = createText('button', '編輯', 'lg-family-address__secondary');
        edit.type = 'button';
        edit.setAttribute('data-family-review-edit', section.key);
        card.append(heading, list, edit);
        grid.appendChild(card);
      });
    }
    var missingRequired = review.filter(function (item) {
      return item.state === 'missing_required';
    });
    var missingOptional = review.filter(function (item) {
      return item.state === 'missing_optional';
    });
    if (banner) {
      banner.hidden = missingRequired.length === 0 && missingOptional.length === 0;
      banner.textContent = missingRequired.length
        ? '還有 ' + missingRequired.length + ' 項必要資料未完成。'
        : missingOptional.length
          ? '有 ' + missingOptional.length + ' 項選填資料未提供；仍可繼續。'
          : '';
    }
    if (summary) {
      summary.replaceChildren();
      summary.append(
        createText('span', '地址資訊 ' + (state.property.confirmedByUser ? '已確認' : '待完成')),
        createText('span', '家庭資料 ' + (state.household.places.length ? '已加入' : '可補充')),
        createText('span', '提交確認 ' + (missingRequired.length ? '尚未完成' : '可以產生結果'))
      );
    }
  }

  function familyPayload(state) {
    return {
      schemaVersion: 1,
      decisionType: state.decisionType,
      compareMode: state.compareMode,
      property: {
        displayAddress: state.property.displayName,
        district: state.property.district,
        latitude: state.property.latitude,
        longitude: state.property.longitude,
        confirmedByUser: state.property.confirmedByUser,
      },
      evidence: {
        listingUrl: state.evidence.listingUrl,
        confirmedFields: Object.keys(state.evidence.fields || {})
          .filter(function (key) {
            return clean(state.evidence.fields[key] && state.evidence.fields[key].value);
          })
          .map(function (key) {
            return { key: key, value: clean(state.evidence.fields[key].value) };
          }),
      },
      household: {
        places: state.household.places.map(function (place) {
          return {
            type: place.type,
            label: place.label,
            address: place.address,
            latitude: place.latitude,
            longitude: place.longitude,
          };
        }),
        travelModes: state.household.travelModes.slice(),
        mobilityPreferences: state.household.mobilityPreferences.slice(),
      },
      priorities: state.priorities.slice(),
      customPriority: state.customPriority,
      acknowledgements: { skippedOptionalFields: state.skippedOptionalFields.slice() },
    };
  }

  function localResult(state) {
    var evidenceCount = Object.keys(state.evidence.fields || {}).filter(function (key) {
      return clean(state.evidence.fields[key] && state.evidence.fields[key].value);
    }).length;
    var hasSchool = state.household.places.some(function (place) {
      return place.type === 'school';
    });
    var hasWork = state.household.places.some(function (place) {
      return place.type === 'work';
    });
    var completeness = 20;
    if (state.property.confirmedByUser) completeness += 25;
    completeness += Math.min(20, evidenceCount * 4);
    completeness += Math.min(20, state.household.places.length * 5);
    completeness += Math.min(15, state.priorities.length * 5);
    completeness = clamp(completeness, 0, 100);

    var categories = RESULT_CATEGORIES.map(function (category) {
      var result = {
        key: category.key,
        label: category.label,
        status: 'unverified',
        conclusion: '需要更多公開資料核實',
        reason: '目前只根據你輸入的資料整理。',
        sources: [],
        limitations: ['尚未完成官方資料核實'],
      };
      if (category.key === 'school_access') {
        result.status = hasSchool ? 'good' : 'insufficient';
        result.conclusion = hasSchool ? '已加入學校位置，可作距離初步比較' : '尚未加入孩子學校';
      } else if (category.key === 'commute_transport') {
        result.status = hasWork || state.household.travelModes.length ? 'good' : 'insufficient';
        result.conclusion = hasWork ? '已加入上班地點，可作距離初步比較' : '尚未加入主要通勤地點';
      } else if (category.key === 'family_facilities') {
        result.status = state.household.places.length >= 2 ? 'good' : 'insufficient';
        result.conclusion =
          state.household.places.length >= 2 ? '已加入多個家庭日常地點' : '家庭日常地點資料較少';
      } else if (category.key === 'walking_safety') {
        result.status =
          state.priorities.indexOf('walking_safety') >= 0 ? 'attention' : 'unverified';
        result.conclusion = '需要官方道路資料與現場情況核實';
      } else if (category.key === 'planning_works') {
        result.status = 'unverified';
        result.conclusion = '尚未取得規劃與工程資料';
      } else if (category.key === 'building_property') {
        result.status = evidenceCount >= 3 ? 'good' : 'insufficient';
        result.conclusion = evidenceCount >= 3 ? '已確認部分樓盤欄位' : '樓盤證據仍不足';
      }
      return result;
    });

    var unresolved = categories
      .filter(function (item) {
        return item.status !== 'good';
      })
      .map(function (item) {
        return item.label + '：' + item.conclusion;
      })
      .slice(0, 5);

    return {
      resultId: uid('family-result'),
      overall: {
        headline:
          completeness >= 70 ? '目前資料較完整，可作初步比較' : '目前可作初步參考，仍需補充資料',
        summary: '這是根據已確認輸入與距離估算整理的快查草稿，不是專業或官方結論。',
        generatedAt: new Date().toISOString(),
      },
      completeness: { percentage: completeness },
      categories: categories,
      unresolvedItems: unresolved,
      sources: [],
      localFallback: true,
    };
  }

  function normalizeRemoteResult(raw, fallback) {
    var data = raw && raw.data && typeof raw.data === 'object' ? raw.data : raw;
    if (!data || typeof data !== 'object') return fallback;
    var result = Object.assign({}, fallback);
    if (data.overall && typeof data.overall === 'object') {
      result.overall.headline = clean(data.overall.headline) || result.overall.headline;
      result.overall.summary = clean(data.overall.summary) || result.overall.summary;
    } else {
      result.overall.headline = clean(data.headline || data.title) || result.overall.headline;
      result.overall.summary = clean(data.summary) || result.overall.summary;
    }
    if (data.completeness && Number.isFinite(Number(data.completeness.percentage))) {
      result.completeness.percentage = clamp(
        Math.round(Number(data.completeness.percentage)),
        0,
        100
      );
    }
    if (Array.isArray(data.categories)) {
      var byKey = {};
      data.categories.forEach(function (item) {
        if (item && item.key) byKey[item.key] = item;
      });
      result.categories = result.categories.map(function (fallbackCategory) {
        var item = byKey[fallbackCategory.key];
        if (!item) return fallbackCategory;
        return {
          key: fallbackCategory.key,
          label: fallbackCategory.label,
          status:
            ['good', 'attention', 'insufficient', 'unverified'].indexOf(item.status) >= 0
              ? item.status
              : fallbackCategory.status,
          conclusion: clean(item.conclusion) || fallbackCategory.conclusion,
          reason: clean(item.reason) || fallbackCategory.reason,
          sources: Array.isArray(item.sources) ? item.sources : [],
          limitations: Array.isArray(item.limitations)
            ? item.limitations
            : fallbackCategory.limitations,
        };
      });
    }
    if (Array.isArray(data.unresolvedItems))
      result.unresolvedItems = data.unresolvedItems.map(clean).filter(Boolean).slice(0, 8);
    if (Array.isArray(data.sources)) result.sources = data.sources.slice(0, 30);
    result.localFallback = false;
    return result;
  }

  function requestResult(root, state) {
    var fallback = localResult(state);
    var executor = window.GCityExecutor;
    if (!executor || typeof executor.execute !== 'function') return Promise.resolve(fallback);
    setStatus(root, '正在取得公開資料與整理結果…');
    return executor
      .execute({
        product_handle: 'family-address-check',
        canonical_endpoint: '/api/decision/analyze',
        api_family: 'gcity-core-api',
        api_tier: 'daily_platform',
        delivery_type: 'tool',
        delivery_method: 'instant',
        engine_type: 'decision_engine',
        result_surface: 'panel',
        ui_mode: 'decision_workspace',
        product_category: 'family',
        service_type: 'b2c',
        requires_gfun: false,
        requires_entitlement: false,
        data: familyPayload(state),
      })
      .then(function (response) {
        if (!response || response.ok !== true) return fallback;
        return normalizeRemoteResult(response, fallback);
      })
      .catch(function () {
        return fallback;
      });
  }

  function renderResult(root, state) {
    var result = state.result || localResult(state);
    state.result = result;
    var headline = root.querySelector('[data-family-result-headline]');
    var copy = root.querySelector('[data-family-result-copy]');
    var completeness = root.querySelector('[data-family-result-completeness]');
    if (headline) headline.textContent = result.overall.headline;
    if (copy) copy.textContent = result.overall.summary;
    if (completeness) completeness.textContent = result.completeness.percentage + '%';
    var grid = root.querySelector('[data-family-result-categories]');
    if (grid) {
      grid.replaceChildren();
      result.categories.forEach(function (category) {
        var button = document.createElement('button');
        button.type = 'button';
        button.className = 'lg-family-address__result-card';
        button.setAttribute('data-result-key', category.key);
        button.setAttribute('data-result-state', category.status);
        button.setAttribute('data-family-result-detail-open', category.key);
        var status =
          category.status === 'good'
            ? '良好'
            : category.status === 'attention'
              ? '需留意'
              : category.status === 'insufficient'
                ? '資料不足'
                : '無法核實';
        button.append(
          createText('strong', category.label),
          createText('span', status),
          createText('small', category.conclusion)
        );
        grid.appendChild(button);
      });
    }
    var unresolved = root.querySelector('[data-family-result-unresolved]');
    var count = root.querySelector('[data-family-unresolved-count]');
    if (unresolved) {
      unresolved.replaceChildren();
      result.unresolvedItems.forEach(function (item) {
        unresolved.appendChild(createText('li', item));
      });
      if (!result.unresolvedItems.length)
        unresolved.appendChild(createText('li', '目前沒有新增待核實項目。'));
    }
    if (count) count.textContent = result.unresolvedItems.length;
    renderResultMap(root, state);
  }

  function renderResultMap(root, state) {
    var center = Number.isFinite(state.property.latitude)
      ? [state.property.latitude, state.property.longitude]
      : MAP_DEFAULT;
    var mapState = createMap(root, 'result', center, null);
    if (!mapState) return;
    mapState.placeMarkers.forEach(function (marker) {
      try {
        mapState.map.removeLayer(marker);
      } catch (_) {}
    });
    mapState.placeMarkers = [];
    if (Number.isFinite(state.property.latitude) && Number.isFinite(state.property.longitude)) {
      mapState.placeMarkers.push(
        window.L.marker([state.property.latitude, state.property.longitude])
          .addTo(mapState.map)
          .bindPopup(escapeHtml('住家位置'))
      );
    }
    state.household.places.forEach(function (place) {
      if (!Number.isFinite(place.latitude) || !Number.isFinite(place.longitude)) return;
      mapState.placeMarkers.push(
        window.L.marker([place.latitude, place.longitude])
          .addTo(mapState.map)
          .bindPopup(escapeHtml(place.label))
      );
    });
    var points = mapState.placeMarkers.map(function (marker) {
      return marker.getLatLng();
    });
    if (points.length > 1) {
      try {
        mapState.map.fitBounds(window.L.latLngBounds(points), { padding: [28, 28] });
      } catch (_) {}
    }
  }

  function renderBrief(root, state) {
    var host = root.querySelector('[data-family-professional-brief]');
    if (!host) return;
    host.replaceChildren();
    var title = document.createElement('div');
    title.className = 'lg-family-address__section-heading';
    title.append(
      createText('h3', 'LittleG 已整理的快查摘要'),
      createText('span', '只讀摘要', 'lg-family-address__badge')
    );
    var dl = document.createElement('dl');
    [
      ['物業地址', state.property.displayName || '未提供'],
      ['主要用途', state.decisionType || '未選擇'],
      [
        '家庭地點',
        state.household.places
          .map(function (place) {
            return place.label;
          })
          .join('、') || '未提供',
      ],
      [
        '關注重點',
        state.priorities
          .map(function (key) {
            return PRIORITIES[key].label;
          })
          .join('、') || '未提供',
      ],
      [
        '待核實項目',
        state.result && state.result.unresolvedItems
          ? state.result.unresolvedItems.length + ' 項'
          : '尚未產生結果',
      ],
    ].forEach(function (item) {
      var row = document.createElement('div');
      row.append(createText('dt', item[0]), createText('dd', item[1]));
      dl.appendChild(row);
    });
    host.append(title, dl);
  }

  function submitCase(root, state) {
    var form = root.querySelector('[data-family-case-contact-form]');
    if (!form || !form.reportValidity()) return;
    var selectedService = root.querySelector('[name="family_service"]:checked');
    if (!selectedService) {
      announce(root, '請先選擇一項專業服務。', 'error');
      return;
    }
    var email = clean(form.elements.email && form.elements.email.value);
    var phone = clean(form.elements.phone && form.elements.phone.value);
    if (!email && !phone) {
      announce(root, '請至少提供電郵或電話其中一項。', 'error');
      if (form.elements.email) form.elements.email.focus();
      return;
    }
    var executor = window.GCityExecutor;
    if (!executor || typeof executor.execute !== 'function') {
      announce(root, '專業服務提交系統尚未載入，資料未送出。', 'error');
      return;
    }
    var button = root.querySelector('[data-family-case-submit]');
    if (button) {
      button.disabled = true;
      button.textContent = '提交中…';
    }
    setStatus(root, '正在安全提交個案…');
    var payload = {
      schemaVersion: 1,
      selectedService: selectedService.value,
      brief: familyPayload(state),
      contact: {
        name: clean(form.elements.name && form.elements.name.value),
        email: email,
        phone: phone,
        questions: clean(form.elements.questions && form.elements.questions.value),
      },
      consent: {
        accepted: !!(form.elements.consent && form.elements.consent.checked),
        acceptedAt: new Date().toISOString(),
        scope: 'professional_follow_up',
        policyVersion: '2026-07-family-address-v1',
      },
    };
    executor
      .execute({
        product_handle: 'family-location-due-diligence',
        canonical_endpoint: '/api/platform/service-lead',
        api_family: 'gcity-platform',
        api_tier: 'professional_technical',
        delivery_type: 'service',
        delivery_method: 'human',
        engine_type: 'service_engine',
        result_surface: 'panel',
        ui_mode: 'decision_workspace',
        product_category: 'family',
        service_type: 'hybrid',
        requires_gfun: false,
        requires_entitlement: true,
        data: payload,
      })
      .then(function (response) {
        if (!response || response.ok !== true) {
          throw new Error(
            clean(response && (response.error || response.reason)) || 'submission_failed'
          );
        }
        var data = response.data && typeof response.data === 'object' ? response.data : response;
        var caseData = data.case && typeof data.case === 'object' ? data.case : data;
        var caseId = clean(caseData.caseId || caseData.case_id || caseData.id);
        if (!caseId) throw new Error('missing_case_id');
        renderCaseSuccess(root, state, caseId, clean(caseData.status) || '已提交');
      })
      .catch(function (error) {
        announce(
          root,
          error && error.message === 'missing_case_id'
            ? '後端未返回正式個案編號，資料未標記為已提交。'
            : '未能建立個案，請稍後再試或使用支援入口。',
          'error'
        );
      })
      .finally(function () {
        if (button) {
          button.disabled = false;
          button.textContent = '提交給廣量';
        }
      });
  }

  function renderCaseSuccess(root, state, caseId, status) {
    var formState = root.querySelector('[data-family-case-form-state]');
    var success = root.querySelector('[data-family-case-state="submitted"]');
    var id = root.querySelector('[data-family-case-id]');
    var stateNode = root.querySelector('[data-family-case-status]');
    if (formState) formState.hidden = true;
    if (success) success.hidden = false;
    if (id) id.textContent = caseId;
    if (stateNode) stateNode.textContent = status;
    state.case = { caseId: caseId, status: status };
    clearDraft();
    announce(root, '個案已建立。', 'success');
  }

  function showResultDetail(root, state, key, openerBtn) {
    var result = state.result || localResult(state);
    var item = result.categories.find(function (category) {
      return category.key === key;
    });
    if (!item) return;
    openPanel(
      root,
      item.label,
      function (body) {
        var status =
          item.status === 'good'
            ? '良好'
            : item.status === 'attention'
              ? '需留意'
              : item.status === 'insufficient'
                ? '資料不足'
                : '無法核實';
        body.append(
          createText('p', status, 'lg-family-address__badge'),
          createText('h4', item.conclusion),
          createText('p', item.reason || '目前沒有更多說明。')
        );
        var limitTitle = createText('h4', '資料限制');
        var list = document.createElement('ul');
        (item.limitations || []).forEach(function (value) {
          list.appendChild(createText('li', clean(value)));
        });
        if (!list.children.length) list.appendChild(createText('li', '未提供額外限制資料。'));
        body.append(limitTitle, list);
      },
      openerBtn
    );
  }

  function showCustomPriority(root, state, openerBtn) {
    openPanel(
      root,
      '其他想確認',
      function (body) {
        var label = document.createElement('label');
        label.className = 'lg-family-address__field';
        label.append(createText('span', '還想了解甚麼？'));
        var textarea = document.createElement('textarea');
        textarea.rows = 5;
        textarea.maxLength = 300;
        textarea.value = state.customPriority || '';
        label.appendChild(textarea);
        var save = createText('button', '保存問題', 'lg-family-address__primary');
        save.type = 'button';
        save.addEventListener('click', function () {
          state.customPriority = clean(textarea.value).slice(0, 300);
          renderPriorities(root, state);
          writeDraft(state);
          closePanel(root);
          announce(root, state.customPriority ? '已加入其他問題。' : '已清除其他問題。', 'success');
        });
        body.append(label, save);
      },
      openerBtn
    );
  }

  function syncEntryState(root, state) {
    var inputs = Array.prototype.slice.call(
      root.querySelectorAll('[name="family_address_decision"]')
    );
    var selected = clean(state.decisionType);
    var matched = false;
    inputs.forEach(function (input) {
      var isSelected = input.value === selected;
      input.checked = isSelected;
      if (isSelected) matched = true;
      input.removeAttribute('aria-invalid');
    });
    if (!matched) state.decisionType = '';

    var action = root.querySelector('[data-family-address-action="start"]');
    if (action) action.disabled = !matched;

    var compare = root.querySelector('[data-family-address-compare]');
    if (compare) compare.setAttribute('aria-pressed', state.compareMode ? 'true' : 'false');
  }

  function showDecisionError(root) {
    var inputs = Array.prototype.slice.call(
      root.querySelectorAll('[name="family_address_decision"]')
    );
    var action = root.querySelector('[data-family-address-action="start"]');
    if (action) action.disabled = true;
    inputs.forEach(function (input) {
      input.setAttribute('aria-invalid', 'true');
    });
    if (inputs[0] && typeof inputs[0].focus === 'function') inputs[0].focus();
  }

  function showCompareHelp(root, state, openerBtn) {
    openPanel(
      root,
      '比較兩個地址',
      function (body) {
        body.append(
          createText(
            'p',
            '比較模式會使用同一組家庭地點與關注重點，比較兩個位置。第一階段先完成主要地址；第二地址會在結果頁加入。'
          )
        );
        var action = createText(
          'button',
          state.compareMode ? '取消比較模式' : '開啟比較模式',
          'lg-family-address__primary'
        );
        action.type = 'button';
        action.addEventListener('click', function () {
          state.compareMode = !state.compareMode;
          writeDraft(state);
          syncEntryState(root, state);
          closePanel(root);
          announce(root, state.compareMode ? '已開啟比較模式。' : '已取消比較模式。', 'success');
        });
        body.appendChild(action);
      },
      openerBtn
    );
  }

  function validateStep(root, state, step) {
    if (step === 'S01') {
      var decision = root.querySelector('[name="family_address_decision"]:checked');
      state.decisionType = clean(decision && decision.value);
      if (!state.decisionType) {
        showDecisionError(root);
        announce(root, '請先選擇主要用途。', 'error');
        return false;
      }
      syncEntryState(root, state);
      return true;
    }
    if (step === 'S02') {
      if (
        !state.property.displayName ||
        !Number.isFinite(state.property.latitude) ||
        !Number.isFinite(state.property.longitude)
      ) {
        announce(root, '請選擇地址並在地圖確認位置。', 'error');
        return false;
      }
      state.property.confirmedByUser = true;
      return true;
    }
    if (step === 'S03') {
      collectEvidence(root, state);
      return true;
    }
    if (step === 'S04') {
      state.household.travelModes = selectedValues(root, 'family_travel_mode');
      state.household.mobilityPreferences = selectedValues(root, 'family_mobility');
      return true;
    }
    if (step === 'S05') {
      if (!state.priorities.length) {
        announce(root, '請至少選擇一項家庭重點。', 'error');
        return false;
      }
      return true;
    }
    if (step === 'S06') {
      var review = buildReview(state);
      if (
        review.some(function (item) {
          return item.state === 'missing_required';
        })
      ) {
        announce(root, '仍有必要資料未完成。', 'error');
        return false;
      }
      return true;
    }
    return true;
  }

  function nextFrom(root, state, step) {
    if (!validateStep(root, state, step)) return;
    if (step === 'S06') {
      var button = root.querySelector('[data-family-address-action="generate-result"]');
      if (button) {
        button.disabled = true;
        button.textContent = '正在整理…';
      }
      requestResult(root, state)
        .then(function (result) {
          state.result = result;
          setScreen(root, state, 'S07');
          announce(
            root,
            result.localFallback
              ? '已顯示保守的本機快查草稿；部分公開資料尚未取得。'
              : '結果已更新。',
            result.localFallback ? '' : 'success'
          );
        })
        .finally(function () {
          if (button) {
            button.disabled = false;
            button.textContent = '確認並產生結果';
          }
        });
      return;
    }
    var index = stepIndex(step);
    setScreen(root, state, STEP_IDS[Math.min(index + 1, STEP_IDS.length - 1)]);
  }

  function bind(root) {
    if (!root || root.getAttribute('data-family-address-bound') === VERSION) return;
    root.setAttribute('data-family-address-bound', VERSION);
    var familyRoot = root.closest(FAMILY_ROOT_SELECTOR);
    if (!familyRoot) return;
    var state = readDraft();
    syncEntryState(root, state);

    var openers = familyRoot.querySelectorAll('[data-family-address-open]');
    Array.prototype.slice.call(openers).forEach(function (button) {
      button.addEventListener('click', function () {
        openWorkflow(familyRoot, root, state);
      });
    });

    var back = root.querySelector('[data-family-address-back]');
    if (back)
      back.addEventListener('click', function () {
        var index = stepIndex(state.step);
        if (index <= 0) return;
        setScreen(root, state, STEP_IDS[index - 1]);
      });

    var saveExit = root.querySelector('[data-family-address-save-exit]');
    if (saveExit)
      saveExit.addEventListener('click', function () {
        writeDraft(state);
        closeWorkflow(familyRoot, root, state, true);
      });

    var search = root.querySelector('[data-family-address-search]');
    if (search) {
      search.value = state.property.query || '';
      search.addEventListener('input', function () {
        state.property.query = clean(search.value);
        renderAddressResults(root, state);
      });
    }

    var district = root.querySelector('[data-family-address-district]');
    if (district)
      district.addEventListener('change', function () {
        state.property.district = clean(district.value);
        renderAddressResults(root, state);
      });

    Array.prototype.slice
      .call(root.querySelectorAll('[data-family-address-hot-search]'))
      .forEach(function (button) {
        button.addEventListener('click', function () {
          state.property.query = clean(button.getAttribute('data-family-address-hot-search'));
          if (search) search.value = state.property.query;
          renderAddressResults(root, state);
        });
      });

    Array.prototype.slice
      .call(root.querySelectorAll('[data-family-evidence-input]'))
      .forEach(function (input) {
        input.addEventListener('change', function () {
          handleEvidenceFile(root, state, input);
        });
      });

    var listingUrl = root.querySelector('[data-family-listing-url]');
    if (listingUrl) {
      listingUrl.value = state.evidence.listingUrl || '';
      listingUrl.addEventListener('change', function () {
        state.evidence.listingUrl = clean(listingUrl.value);
      });
    }

    Array.prototype.slice
      .call(root.querySelectorAll('[data-family-evidence-field] input'))
      .forEach(function (input) {
        input.addEventListener('change', function () {
          collectEvidence(root, state);
        });
      });

    var placeForm = root.querySelector('[data-family-place-form]');
    if (placeForm) {
      placeForm.addEventListener('submit', function (event) {
        event.preventDefault();
        var mapState = getMapStore(root).routes;
        if (!mapState || !mapState.pendingLatLng) {
          announce(root, '請先在地圖點選地點位置。', 'error');
          return;
        }
        var label = clean(placeForm.elements.label.value);
        if (!label) return;
        state.household.places.push({
          id: uid('place'),
          type: clean(placeForm.elements.type.value),
          label: label,
          address: clean(placeForm.elements.address.value),
          latitude: mapState.pendingLatLng.latitude,
          longitude: mapState.pendingLatLng.longitude,
        });
        if (mapState.pendingMarker) {
          try {
            mapState.map.removeLayer(mapState.pendingMarker);
          } catch (_) {}
          mapState.pendingMarker = null;
        }
        mapState.pendingLatLng = null;
        var add = root.querySelector('[data-family-place-add]');
        if (add) add.disabled = true;
        placeForm.reset();
        renderPlaces(root, state);
        announce(root, '已加入家庭地點。', 'success');
      });
    }

    root.addEventListener('change', function (event) {
      var decision =
        event.target &&
        event.target.matches &&
        event.target.matches('[name="family_address_decision"]')
          ? event.target
          : null;
      if (decision) {
        state.decisionType = clean(decision.value);
        writeDraft(state);
        syncEntryState(root, state);
        announce(root, '已選擇主要用途。', 'success');
        return;
      }

      var priority =
        event.target &&
        event.target.matches &&
        event.target.matches('[name="family_address_priority"]')
          ? event.target
          : null;
      if (!priority) return;
      var selected = selectedValues(root, 'family_address_priority');
      if (selected.length > MAX_PRIORITIES) {
        priority.checked = false;
        announce(root, '最多選擇 3 項，請先取消其中一項。', 'error');
        selected = selectedValues(root, 'family_address_priority');
      }
      state.priorities = selected;
      renderPriorities(root, state);
    });

    root.addEventListener('click', function (event) {
      var action =
        event.target && event.target.closest
          ? event.target.closest('[data-family-address-action]')
          : null;
      if (action) {
        var type = clean(action.getAttribute('data-family-address-action'));
        var map = {
          start: 'S01',
          'confirm-address': 'S02',
          'confirm-evidence': 'S03',
          'confirm-places': 'S04',
          'confirm-priorities': 'S05',
          'generate-result': 'S06',
          'professional-handoff': 'S07',
        };
        if (map[type]) nextFrom(root, state, map[type]);
        return;
      }

      var candidate =
        event.target && event.target.closest
          ? event.target.closest('[data-family-address-candidate]')
          : null;
      if (candidate) {
        selectAddressCandidate(
          root,
          state,
          candidate.getAttribute('data-family-address-candidate')
        );
        return;
      }

      var remove =
        event.target && event.target.closest
          ? event.target.closest('[data-family-place-remove]')
          : null;
      if (remove) {
        var id = clean(remove.getAttribute('data-family-place-remove'));
        state.household.places = state.household.places.filter(function (place) {
          return place.id !== id;
        });
        renderPlaces(root, state);
        announce(root, '地點已移除。');
        return;
      }

      var compare =
        event.target && event.target.closest
          ? event.target.closest('[data-family-address-compare]')
          : null;
      if (compare) {
        showCompareHelp(root, state, compare);
        return;
      }

      var custom =
        event.target && event.target.closest
          ? event.target.closest('[data-family-custom-priority-open]')
          : null;
      if (custom) {
        showCustomPriority(root, state, custom);
        return;
      }

      var resultDetail =
        event.target && event.target.closest
          ? event.target.closest('[data-family-result-detail-open]')
          : null;
      if (resultDetail) {
        showResultDetail(
          root,
          state,
          resultDetail.getAttribute('data-family-result-detail-open'),
          resultDetail
        );
        return;
      }

      var refresh =
        event.target && event.target.closest
          ? event.target.closest('[data-family-result-refresh]')
          : null;
      if (refresh) {
        refresh.disabled = true;
        refresh.textContent = '整理中…';
        requestResult(root, state)
          .then(function (result) {
            state.result = result;
            renderResult(root, state);
            writeDraft(state);
          })
          .finally(function () {
            refresh.disabled = false;
            refresh.textContent = '重新整理結果';
          });
        return;
      }

      var reviewEdit =
        event.target && event.target.closest
          ? event.target.closest('[data-family-review-edit]')
          : null;
      if (reviewEdit) {
        var targetStep = { address: 'S02', property: 'S03', places: 'S04', priorities: 'S05' }[
          reviewEdit.getAttribute('data-family-review-edit')
        ];
        if (targetStep) setScreen(root, state, targetStep);
        return;
      }

      var submit =
        event.target && event.target.closest
          ? event.target.closest('[data-family-case-submit]')
          : null;
      if (submit) {
        submitCase(root, state);
        return;
      }

      var copy =
        event.target && event.target.closest
          ? event.target.closest('[data-family-case-copy]')
          : null;
      if (copy && state.case && state.case.caseId) {
        var operation =
          navigator.clipboard && navigator.clipboard.writeText
            ? navigator.clipboard.writeText(state.case.caseId)
            : Promise.reject();
        operation
          .then(function () {
            announce(root, '個案編號已複製。', 'success');
          })
          .catch(function () {
            announce(root, '請長按個案編號手動複製。');
          });
        return;
      }

      var close =
        event.target && event.target.closest
          ? event.target.closest('[data-family-address-close]')
          : null;
      if (close) {
        closeWorkflow(familyRoot, root, state, false);
        return;
      }

      var panelClose =
        event.target && event.target.closest
          ? event.target.closest('[data-family-address-panel-close]')
          : null;
      if (
        panelClose ||
        event.target === root.querySelector('[data-family-address-panel-backdrop]')
      ) {
        closePanel(root);
      }
    });

    // 鍵盤 Escape 事件關閉面板
    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        var panel = root.querySelector('[data-family-address-panel]:not([hidden])');
        if (panel) closePanel(root);
      }
    });

    if (state.priorities.length) renderPriorities(root, state);
    if (state.household.places.length) renderPlaces(root, state);
    var params;
    try {
      params = new URL(window.location.href).searchParams;
    } catch (_) {
      params = null;
    }
    if (params && params.get('intent') === 'family-address-check')
      openWorkflow(familyRoot, root, state);
  }

  function boot(scope) {
    Array.prototype.slice.call((scope || document).querySelectorAll(ROOT_SELECTOR)).forEach(bind);
  }

  // 解綁與釋放地圖實例 (Memory Leak Fix)
  document.addEventListener('shopify:section:unload', function (event) {
    var root = event.target ? event.target.querySelector(ROOT_SELECTOR) : null;
    if (!(root instanceof HTMLElement)) return;
    var store = getMapStore(root);
    Object.keys(store).forEach(function (key) {
      if (store[key] && store[key].map) {
        try {
          store[key].map.remove();
        } catch (_) {}
      }
    });
    root.__familyAddressMaps = {};
    root.removeAttribute('data-family-address-bound');
  });

  window.LittleGFamilyAddressCheck = Object.freeze({
    VERSION: VERSION,
    open: function () {
      var root = document.querySelector(ROOT_SELECTOR);
      var familyRoot = root && root.closest(FAMILY_ROOT_SELECTOR);
      if (!root || !familyRoot) return false;
      openWorkflow(familyRoot, root, readDraft());
      return true;
    },
    clearDraft: clearDraft,
  });

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      function () {
        boot(document);
      },
      { once: true }
    );
  } else {
    boot(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    boot(event.target || document);
  });
})(window, document);
